/**
 * novaCrouch.ts
 * Crouch state and the shrunk physics collider that goes with it.
 *
 * Crouching is a traversal move, not just a speed cap: the collider drops its
 * top edge so Nova fits through two-tile gaps. Two rules make that safe.
 *
 * 1. The feet never move. The collider shrinks from the head down, so crouching
 *    can't drop Nova through the floor she is standing on.
 * 2. Releasing crouch under a ceiling does nothing. Standing up is a request,
 *    not a command — if the full-height box would intersect solid geometry she
 *    stays crouched until there is headroom. Without this, letting go of down
 *    inside a gap would pop the collider into the ceiling and let the next
 *    collision pass eject Nova through it.
 *
 * @module novaCrouch
 */

import { aabbOverlaps } from './collision';
import { NOVA_WIDTH, NOVA_HEIGHT, NOVA_CROUCH_HEIGHT } from '../../constants/gameConstants';
import type { AABB } from '../types/entity';
import type { NovaRuntime } from './gameStateTypes';

/**
 * Distance the collider's top edge drops while crouched.
 *
 * @param isCrouching - Whether the crouched collider is in effect.
 * @returns Offset in pixels to add to `nova.y` to get the collider's top edge.
 */
export function crouchTopOffset(isCrouching: boolean): number {
  return isCrouching ? NOVA_HEIGHT - NOVA_CROUCH_HEIGHT : 0;
}

/**
 * Test whether Nova's full-height box fits at her current position.
 *
 * One-way platforms are excluded on purpose: they are passable from below, so
 * one overhead can't be what traps Nova in a crouch.
 *
 * @param nova - Nova's runtime state, read for position only.
 * @param platforms - Solid platforms to test against.
 * @returns True when nothing solid occupies the standing box.
 */
export function canStandUp(nova: NovaRuntime, platforms: readonly AABB[]): boolean {
  const standing: AABB = { x: nova.x, y: nova.y, width: NOVA_WIDTH, height: NOVA_HEIGHT };
  for (const platform of platforms) {
    if (aabbOverlaps(standing, platform)) return false;
  }
  return true;
}

/**
 * Update `nova.isCrouching` for this tick.
 *
 * @param nova - Nova's runtime state; `isCrouching` is written.
 * @param platforms - Solid platforms, used for the headroom test.
 * @param wantsCrouch - Whether the crouch input conditions are met this tick.
 */
export function resolveCrouchState(
  nova: NovaRuntime,
  platforms: readonly AABB[],
  wantsCrouch: boolean,
): void {
  if (wantsCrouch) {
    nova.isCrouching = true;
    return;
  }
  if (nova.isCrouching !== true) return;
  // DECISION: stay crouched when blocked rather than forcing the stand and
  // letting collision resolution push Nova out — that push has no correct
  // direction inside a one-tile gap and ejects her through the ceiling.
  nova.isCrouching = !canStandUp(nova, platforms);
}
